import {css, cx} from '@emotion/css';
import { Location } from 'Common/Location';
import {Checkbox, Html, SingleSelect, Text} from 'Shared/forms/Inputs';
import { LabelOnLeftWrap, labelStyle, Wrap } from 'Shared/forms/Wrap';
import {StoreRepeater, addItemAndOpen, deleteItem} from 'Shared/forms/StoreRepeater';
import {MenuDoc} from 'Shared/model/Menu';
import {createResource, Show, Suspense} from 'solid-js';
import {For,Switch,Match,createSignal} from 'solid-js';
import { subdoc} from 'Common/ViewUtils';
import { repeaterColour } from 'Shared/forms/Repeater';
import { theme } from 'Shared/ThemeProvider';
import { useServer } from 'Shared/SiteProvider';
import { ConnectionToServer } from 'Shared/Root';
import { CreateStoreProvider, IStoreWrap, StoreProvider, useStore } from 'Shared/StoreProvider';
import { useParams } from '@solidjs/router';
import { StyleTagContents } from 'Shared/backend/StyleTags';


export const productTypes = {
	food: 'Food',
	drinks: 'Drink',
	beers: 'Beer',
	wines: 'Wine'
};

export const sectionProductTypes = {
	mixed: 'Mixed',
	food: 'Food',
	drinks: 'Drinks',
	beers: 'Beers',
	wines: 'Wines'
};

const htmlEditorOptions = {
	height: 200,
	menubar: false,
	plugins: ['link'],
	toolbar: 'removeformat | undo redo | bold italic underline | alignleft aligncenter alignright alignjustify | outdent indent | link ',
	statusbar: true,
	resize: true 
};

const verticalStyle = () => css({
	display: 'flex',
	flexDirection: 'column',
	gap: '1.5em'
});

const menuDetailsStyle = () => css(verticalStyle(),{
	paddingLeft: '1em',
	paddingTop: '1em'
});

const sectionsHeadingStyle = () => css(labelStyle(), {
	display: 'block',
	marginTop: '2em'
});

const buttonRowStyle = () => css({
	display: 'flex',
	gap: '0.3em'
});

const defaultSectionFields = () => ({productType:'mixed'});



export type MenuDesignerProps = MenuDoc & { 
	setStore: (...args: any[]) => void,
};


const pageName = 'backend/MenuDesigner';


export default function MenuDesignerPage()
{		
	const server = useServer();
	const params = useParams();

	const [menus] = createResource(() => server.list.query({page:pageName,permission:'menus',params:params}));

	return (
		<Suspense>
			<Show when={menus()}>
				<CreateStoreProvider server={server} page={pageName} permission='menus' initial={menus()} docId={menus()._id} location={[]}>
					<Contents />
				</CreateStoreProvider>
			</Show>
		</Suspense>
	);
}

function Contents()
{
	const menuTitleStyle = css(theme().a,{
		display: 'block',
		fontSize: 30,
		margin: '20px 0',

		color: theme().palette.text,
		'&:visited': {
			color: theme().palette.text
		}
	});

	const [current,setCurrent] = createSignal(undefined);
	const server = useServer();
	const setMenus = useStore().setStore;
	const menus = useStore().store as MenuDoc[];

	return (
		<>
			<p>
				<a class={css(theme().a)} href='/admin/menus'>Go to menu items</a>
			</p>

			<div>
				<h1>Menu Designer</h1>				

				<Show when={menus.length > 0} fallback={<p>There are no menus yet</p>}>
					Available menus:
				</Show>

				<For each={menus}>
					{(menu,index) => (
						<Switch>
							<Match when={index()==current()}>
								<a href='' onClick={() => setCurrent(undefined)} class={menuTitleStyle}>&#x2795; {menu.title}</a> 
								<Menu index={index()} setCurrent={setCurrent} menus={menus} setMenus={setMenus} />
							</Match>
							<Match when={true}>
								<a href=''onClick={() => setCurrent(index())} class={menuTitleStyle}>&#x2796; {menu.title}</a>
							</Match>
						</Switch>
					)}
				</For>

				<p>
					<a class={css(theme().a)} href='' onClick={() => createMenu(server,menus,setMenus,setCurrent)}>Add menu</a>
				</p>
			</div>
		</>
	);
}



function Menu(P:MenuDoc & {index:number,setCurrent,menus,setMenus})
{
	const server = useServer();
	const menu = P.menus[P.index];

	return (
		<CreateStoreProvider server={server} page={pageName} permission='edit' initial={menu} docId={menu._id} location={[]}>
			<MenuContents index={P.index} setCurrent={P.setCurrent} menus={P.menus} setMenus={P.setMenus} {...menu} />
		</CreateStoreProvider>
	);
}

function MenuContents(P:MenuDoc & {index:number,setCurrent,menus,setMenus})
{
	const server = useServer();
	const sw = useStore();
	const [showDetails,setShowDetails] = createSignal(false);

	const url = () => '/menu/'+P.index;

	const title = i => typeof i.widget != 'undefined' ? 'WIDGET: '+widgetOptions[i.widget] : i.title;

	let itemsNode!:HTMLElement;

	return (
		<div>
			<a class={css(theme().a)} onClick={() => setShowDetails(!showDetails())}>
				<i class={cx('fas',showDetails() ? 'fa-caret-down': 'fa-caret-right')} /> 
				&nbsp;
				{showDetails() ? 'Hide menu details' : 'Show menu details'} 
			</a>

			<Show when={showDetails()}>
				<MenuFields />
			</Show>

			<p>
				<Show when={P.sections?.length >= 1}>
					<label class={sectionsHeadingStyle()}>Sections</label>
				</Show>

				<StoreRepeater ref={itemsNode} field='sections' renderTitle={title} setCurrent={P.setCurrent}
					deleteItem={(event,index:number) => deleteSection(event,sw,[],index)}
				>
					{(item,index) => 
						<StoreProvider {...sw} location={['sections',index]}>
							<SectionsAndWidgets url={url()} index={index} {...item} />
						</StoreProvider>
					}
				</StoreRepeater>
			</p>

			<div class={buttonRowStyle()}>
				<button class={css(theme().button)}
					onClick={() => addItemAndOpen(sw,'sections',itemsNode,defaultSectionFields())}
				>
					<i class='fas fa-plus'/> Add section
				</button>

				<button class={css(theme().button)}
					onClick={() => addItemAndOpen(sw,'sections',itemsNode,{widget:'buttonRow'})}
				>
					<i class='fas fa-plus'/> Add widget
				</button>
			</div>

			<p><a class={css(theme().a)} href='' onClick={() => deleteMenu(server,P.menus,P.setMenus,P,P.setCurrent)}>Delete menu</a></p>
		</div>
	);
}

async function deleteSection(event:Event,sw:IStoreWrap<MenuDoc>,location:Location,index:number)
{
	event.stopPropagation();

	const toDelete = subdoc(sw.store,[...location,'sections',index]);
	if (toDelete.sections?.length > 0 || toDelete.items?.length > 0) {
		alert('You need to delete out the menu items, subsections and widgets first');
		return;
	}

	if (!confirm('Are you sure you wish to delete this item?'))
		return;

	await deleteItem(sw,'sections',index);
}

function SectionsAndWidgets(P:{index:number,url:string})
{
	const sectionStyle = css({
		borderLeft: `5px solid ${repeaterColour}`,
		paddingLeft: 8,
		display: 'flex',
		flexDirection: 'column',
		gap: '1em'
	});

	return <div class={sectionStyle}>
		<Switch>
			<Match when={typeof P.widget != 'undefined'}>
				<WidgetFields {...P} />
			</Match>

			<Match when={true}>
				<SectionContents {...P} index={P.index} url={P.url} />
			</Match>
		</Switch>
	</div>
}

function MenuFields()
{
	return (
		<div class={menuDetailsStyle()}>
			<Wrap label='Menu title' required={true}>
				<Text field='title' />
			</Wrap>

{/* XXX handle 'hide title' in styles instead? */}			
			<LabelOnLeftWrap label='Hide title on the menu' required={false}>
				<Checkbox field='hideTitle' />
			</LabelOnLeftWrap>

			<StyleTagContents />

			{/* TODO auto-populate by default. cf having an enable checkbox */}
			<Wrap label='URL slug' required={true}>
				<Text field='slug' required={true} />
			</Wrap>

			<LabelOnLeftWrap label='Product type' required={true} disable={true}>
				<SingleSelect field='productType' options={sectionProductTypes} required={true} />
			</LabelOnLeftWrap>

			<Wrap label='Teaser'>
				<Text field='teaser' placeholder='Optional'/>
			</Wrap>

			<Wrap label='Tagline'>
				<Text field='tagline' placeholder='Optional'/>
			</Wrap>

			<Wrap label='Details'>
				<Html field='details' placeholder='Optional' options={htmlEditorOptions} />
			</Wrap>
		</div>
	);
}

const widgetOptions = {buttonRow:'Button Row',specials:'Specials'};

function WidgetFields(P)
{
	return (<>
		<Wrap label='Widget type' required={true}>
			<SingleSelect field='widget' required={true} options={widgetOptions} />
		</Wrap>

		{/* TODO possible require 'sticky' to be selected from styles */}
		<StyleTagContents />

		<Switch>
			<Match when={P.widget == 'buttonRow'}>
				<ButtonRow />
			</Match>
			<Match when={P.widget == 'specials'}>
				Note: the anchor to link a button to this is "#specials"
			</Match>
		</Switch>
	</>);
}

function ButtonRow()
{
	const sw = useStore();
	let itemsNode!: HTMLElement;

	/* XXX
		'Menu Shortcuts' vs 'Shortcuts' - I'm thinking menu shortcuts might have the food/drinks colour distinction.
		The stickiness could potentially be subsumed within 'Menu Shortcuts'.
	 */
	const rowStyleOptions = {
		plain: 'Plain',
		food: 'Food',
		drinks: 'Drinks',
	};

	return (
		<>
			<Wrap label='Sticky'>
				<Checkbox field='sticky' />
			</Wrap>

			<div>
				<StoreRepeater ref={itemsNode} field='buttons' renderTitle={i => i.label } >
					{(item,index) => 
						<StoreProvider {...sw} location={[...sw.location,'buttons',index]}>
							<div class={verticalStyle()}>
								<Wrap label='Label'>
									<Text field='label' required={true} />
								</Wrap>

								<Wrap label='Style'>
									<SingleSelect field='style' required={true} options={rowStyleOptions} />
								</Wrap>

								<Wrap label='URL' >
									<Text field='url' required={true} placeholder='eg #Entrees if you want button to jump to the Entrees section of the Menu'/>
								</Wrap>
							</div>
						</StoreProvider>
					}
				</StoreRepeater>

				<button class={css(theme().button)}
					onClick={() => addItemAndOpen(sw,'buttons',itemsNode,{})}
				>
					<i class='fas fa-plus'/> Add button
				</button>
			</div>
		</>
	);
}

function SectionContents(P: {index:number,url:string})
{
	let itemsNode!:HTMLElement;
	const sw = useStore();

	const url = () => P.url+'/'+P.index;
	const title = i => typeof i.widget != 'undefined' ? 'WIDGET: '+widgetOptions[i.widget] : i.title;

	return <>
		<SectionFields {...P} />

		<label class={sectionsHeadingStyle()}>{sw.location.length==0  ? 'Sections' : 'Subsections'}</label>

		<div>
			<StoreRepeater ref={itemsNode} field='sections' renderTitle={title} 
				deleteItem={(event,index:number) => deleteSection(event,sw,sw.location,index)}
			>
				{(item,index) => 
					<StoreProvider {...sw} location={[...sw.location,'sections',index]}>
						<SectionsAndWidgets url={url()} index={index} {...item} />
					</StoreProvider>
				}
			</StoreRepeater>

			<div class={buttonRowStyle()}>
				<button class={css(theme().button)}
					onClick={() => addItemAndOpen(sw,'sections',itemsNode,defaultSectionFields())}
				>
					<i class='fas fa-plus'/> Add section
				</button>

				<button class={css(theme().button)}
					onClick={() => addItemAndOpen(sw,'sections',itemsNode,{widget:'buttonRow'})}
				>
					<i class='fas fa-plus'/> Add widget
				</button>
			</div>
		</div>
		{/*TODO add a more cautious delete*/}
	</>;
}

function SectionFields()
{
	const singleLineStyle = css({
		display: 'flex',
		alignItems: 'center',
		gap: 10
	});

	const storeWrap = useStore();

	const parentProductType = () => calculateProductType(storeWrap.store,storeWrap.location.slice(0,-1));  //TODO memoise

	return (<>
		<Wrap label='Title' required={true}>
			<Text field='title' required={true} />
		</Wrap>

		<Switch>
			<Match when={parentProductType() == 'mixed'}>
				<Wrap label='Product type' required={true}>
					<SingleSelect field='productType' options={sectionProductTypes} required={true} />
				</Wrap>
			</Match>
			<Match when={true}>
				<Wrap class={singleLineStyle} label='Product type' disable={true}>
					<div>{sectionProductTypes[parentProductType()]}</div>
				</Wrap>
			</Match>
		</Switch>
						
		<StyleTagContents />

		<Wrap label='Teaser'>
			<Text field='teaser' placeholder='Optional' />
		</Wrap>

		<Wrap label='Tagline'>
			<Text field='tagline' placeholder='Optional'/>
		</Wrap>

		<Wrap label='Details'>
			<Html field='details' placeholder='Optional' options={htmlEditorOptions} />
		</Wrap>
	</>);
}

//FIXME use createResource() I guess (necessary?)
async function createMenu(server:ConnectionToServer,menusStore,setMenusStore,setCurrent)
{
	const {id} = await server.create.mutate({page:pageName,permission:'createMenu',fields:defaultSectionFields()});

	const numMenus = menusStore.length;
	setMenusStore([...menusStore, {_id:id,...defaultSectionFields()}]);
	setCurrent(numMenus);
}

async function deleteMenu(server:ConnectionToServer,menusStore,setMenusStore,store:MenuDoc,setCurrent)
{
//TODO probably have an archive option instead. Maybe allow deletion after a day or something.

	const sections = store.sections ?? [];
	const items = store.items ?? [];

	if (sections.length > 0 || items.length > 0) {
		alert('You need to delete out the menu items, subsections and widgets first');
		return;
	}

	if (!confirm('Are you sure you wish to delete this menu?'))
		return;

    await server.delete.mutate({page:pageName,permission:'deleteMenu',id:store._id});

	const i = menusStore.findIndex(e => e._id == store._id);
	setMenusStore([...menusStore.slice(0,i), ...menusStore.slice(i+1)]);
	setCurrent(undefined);
}

export function calculateProductType(top:MenuDoc,location:Location)
{
	if (top.productType!=undefined && top.productType!='mixed')
		return top.productType;

	let data = top;
	for (let i=0; i<location.length; i++) {
		data = data[location[i]];

		if (data.productType!=undefined && data.productType!='mixed')
			return data.productType;
	}

	return 'mixed';
}

